import React, { useEffect, useState } from 'react';
import { Grid, Input, Table } from 'antd';
import { useTranslation } from 'react-i18next';
import axiosInstance from "../../axios/axiosInstance";
import { MdHistory } from "react-icons/md";
import { DataTable, PageCard, PageShell, PageToolbar } from "../../components/ui";

const { useBreakpoint } = Grid;

const Payments = ({ patientId }) => {
    const { t } = useTranslation();
    const screens = useBreakpoint();
    const sum = (value) => `${value} ${t('common.currency')}`;

    const columns = [
        {
            title: t('payments.date'),
            dataIndex: 'date_at',
            width: 148,
            render: (text) => (
                <div className="flex items-center gap-1.5 text-sm text-slate-700 tabular-nums whitespace-nowrap">
                    <MdHistory size="16" className="text-slate-400 shrink-0" /> {text}
                </div>
            ),
            sorter: true,
        },
        {
            title: t('payments.serviceNumber'),
            dataIndex: 'serviceId', // Новое поле, которое мы будем добавлять в dataSource
            width: 148,
            render: (text) => <span className="text-xs text-slate-500 tabular-nums">{text}</span>,
        },
        {
            title: t('common.patient'),
            dataIndex: 'patientName', // Новое поле, которое мы будем добавлять в dataSource
            ellipsis: true,
            render: (text) => <span className="text-sm font-medium text-[#1D2C7B]">{text}</span>,
        },

        {
            title: t('payments.total'),
            dataIndex: 'total_amount',
            width: 160,
            align: 'right',
            render: (text) => (
                <span className="text-sm text-slate-700 tabular-nums whitespace-nowrap">{sum(text)}</span>
            ),
            sorter: true,
        }
        ,{
            title: t('payments.paid'),
            dataIndex: 'total_payed',
            width: 160,
            align: 'right',
            render: (text) => (
                <span className="text-sm text-slate-700 tabular-nums whitespace-nowrap">{sum(text)}</span>
            ),
            sorter: true,
        }, {
            title: t('payments.left'),
            dataIndex: 'debit',
            width: 150,
            align: 'right',
            render: (text) => (
                <span className="text-sm font-medium text-slate-900 tabular-nums whitespace-nowrap">{sum(text)}</span>
            ),
            sorter: true,
        },
    ];

    const isPhone = screens.md === false;
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [tableParams, setTableParams] = useState({
        pagination: {
            current: 1,
            pageSize: 10,
        },
    });

    useEffect(() => {
        fetchData();
    }, [tableParams.pagination.current, tableParams.pagination.pageSize, patientId]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/visit?page=${tableParams.pagination.current}`);
            const visits = response.data.data;
            // Фильтруем визиты, чтобы оставить только те, где есть услуги с service_type: true
            const filteredVisits = visits.filter(visit =>
                visit.orders.some(order => order.service_type === true)
            ).map(visit => ({
                ...visit,
                serviceId:   visit.orders.find(order => order.service_type === true)?.id.slice(-12),
                patientName: visit.patient_id.name,
            }));
            setData(filteredVisits.reverse());
            setTableParams({
                ...tableParams,
                pagination: {
                    ...tableParams.pagination,
                    total: response.data.meta.total,
                },
            });
        } catch (error) {
            console.error('Error fetching visits:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleTableChange = (pagination, filters, sorter) => {
        setTableParams({
            pagination,
            filters,
            ...sorter,
        });
    };

    const transactionTypeLabels = {
        cash: t('payments.cash'),
        card: t('payments.card'),
        bank: t('payments.bank'),
        online: t('payments.online')
        // Добавьте другие типы транзакций, если они есть
    };

    const renderMobileCard = (record) => (
        <div>
            <div className="flex items-start justify-between gap-2">
                <span className="min-w-0 truncate text-sm font-semibold text-[#1D2C7B]">
                    {record.patientName}
                </span>
                <span className="flex shrink-0 items-center gap-1 text-xs text-slate-500 tabular-nums">
                    <MdHistory size="14" className="text-slate-400" /> {record.date_at}
                </span>
            </div>

            <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-2">
                <div className="min-w-0">
                    <span className="block text-[11px] leading-tight text-slate-500">{t('payments.total')}</span>
                    <span className="block text-sm text-slate-700 tabular-nums">{sum(record.total_amount)}</span>
                </div>
                <div className="min-w-0">
                    <span className="block text-[11px] leading-tight text-slate-500">{t('payments.paid')}</span>
                    <span className="block text-sm text-slate-700 tabular-nums">{sum(record.total_payed)}</span>
                </div>
                <div className="min-w-0">
                    <span className="block text-[11px] leading-tight text-slate-500">{t('payments.left')}</span>
                    <span className="block text-sm font-medium text-slate-900 tabular-nums">{sum(record.debit)}</span>
                </div>
            </div>
        </div>
    );

    return (
        <PageShell>
            <PageCard>
                <PageToolbar
                    title={t('payments.title')}
                    count={tableParams.pagination.total}
                    filters={
                        <Input.Search
                            size="small"
                            placeholder={t('payments.search')}
                            allowClear
                            enterButton={t('payments.searchBtn')}
                            className="w-full md:!w-[280px]"
                        />
                    }
                />

                <DataTable
                    columns={columns}
                    dataSource={data}
                    mobileCard={renderMobileCard}
                    scrollX={1100}
                    pagination={{
                        ...tableParams.pagination,
                        showSizeChanger: false,
                        position: ['bottomCenter'],
                        className: 'px-4 pb-3',
                    }}
                    loading={loading}
                    onChange={handleTableChange}
                    expandable={{
                        showExpandColumn: !isPhone,
                        expandRowByClick: isPhone,
                        expandedRowRender: (record) => (
                            <Table
                                size="small"
                                scroll={{ x: 720 }}
                                columns={[
                                    {
                                        title: t('patients.colId'),
                                        dataIndex: 'index',
                                        key: 'index',
                                        width: 56,
                                        render: (text, record, index) => (
                                            <span className="text-xs text-slate-500 tabular-nums">{index + 1}</span>
                                        )
                                    },
                                    {
                                        title: t('payments.serviceName'),
                                        dataIndex: 'serviceName',
                                        key: 'serviceName',
                                        render: (text) => <span className="text-sm text-slate-700">{text}</span>
                                    },
                                    {
                                        title: t('payments.serviceCount'),
                                        dataIndex: 'serviceCount',
                                        key: 'serviceCount',
                                        width: 110,
                                        render: (text) => <span className="text-sm text-slate-700 tabular-nums">{text}</span>
                                    },
                                    {
                                        title: t('payments.txAmount'),
                                        dataIndex: 'amount',
                                        key: 'amount',
                                        width: 170,
                                        align: 'right',
                                        render: (text) => (
                                            <span className="text-sm text-slate-700 tabular-nums whitespace-nowrap">{text}</span>
                                        )
                                    },
                                    {
                                        title: t('payments.txType'),
                                        dataIndex: 'type',
                                        key: 'type',
                                        width: 150,
                                        render: (text) => (
                                            <span className="text-sm text-slate-700">{transactionTypeLabels[text] || text}</span>
                                        )
                                    },
                                    {
                                        title: t('payments.txDate'),
                                        dataIndex: 'created_at',
                                        key: 'created_at',
                                        width: 180,
                                        render: (text) => (
                                            <span className="text-xs text-slate-500 tabular-nums whitespace-nowrap">
                                                {new Date(text).toLocaleString()}
                                            </span>
                                        )
                                    },
                                ]}
                                dataSource={record.orders.map(order => ({
                                    ...order,
                                    serviceName: order.service.name, // Добавляем поле для имени сервиса
                                    transactions: order.transactions.map(transaction => ({
                                        ...transaction,
                                        serviceName: order.service.name,
                                        serviceCount: order.count,
                                        amount: sum(transaction.amount),
                                    }))
                                })).flatMap(order => order.transactions)}
                                pagination={false}
                                rowKey="created_at"
                            />
                        ),
                        rowExpandable: (record) => {
                            return record.orders && record.orders.some(order => order.transactions && order.transactions.length > 0);
                        }

                    }}
                    rowKey={(record) => record.id}
                />
            </PageCard>
        </PageShell>
    );
};

export default Payments;
